import React, { useEffect, useState } from 'react';
import api from '../utils/api';
import { Plus, Search, Printer, Edit, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

// Printable/selectable columns for each packing mode. "The fields which we
// select they should display in the final packing list."
const BOX_COLS = [
  { key: 'num_boxes',         label: 'Number of Boxes' },
  { key: 'bundles',           label: 'Numbers of Bundles (A)' },
  { key: 'pieces_per_bundle', label: 'No. of pieces per bundle (B)' },
  { key: 'total_qty',         label: 'Total quantity (A)*(B)' },
  { key: 'net_weight',        label: 'Total Net Weight (Kg)' },
  { key: 'gross_weight',      label: 'Total weight of the Bundle (Kg)' },
];
const ROLL_COLS = [
  { key: 'number_of_rolls', label: 'Number of Rolls' },
  { key: 'net_weight',      label: 'Net Weight (Kg)' },
  { key: 'shipper_weight',  label: 'Shipper Weight (Kg)' },
  { key: 'gross_weight',    label: 'Gross Weight (Kg)' },
];
const colsForMode = (mode) => (mode === 'roll' ? ROLL_COLS : BOX_COLS);

const PackingList = () => {
  const [lists, setLists] = useState([]);
  const [orders, setOrders] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [companySettings, setCompanySettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedOrder, setSelectedOrder] = useState(null);

  const blankRow = (n = 1) => ({ box_number: n, roll_number: '', num_boxes: '', number_of_rolls: '', bundles: '', pieces_per_bundle: '', total_qty: '', net_weight: '', tare_weight: '', shipper_weight: '', gross_weight: '' });
  const emptyForm = {
    sales_order_id: '', customer_id: '', customer_name: '', customer_address: '',
    product_name: '', product_size: '', batch_number: '', total_quantity: '', date: '', pack_mode: 'box',
    items: [], box_rows: [blankRow(1)], print_fields: null
  };
  const [formData, setFormData] = useState({ ...emptyForm });

  // Build a printable multi-line address from the customer's billing address.
  const formatAddress = (a) => {
    if (!a) return '';
    if (typeof a === 'string') return a;
    return [
      a.address_line1, a.address_line2,
      [a.city, a.state].filter(Boolean).join(', '),
      [a.pincode, a.country].filter(Boolean).join(' ')
    ].map(s => (s || '').trim()).filter(Boolean).join('\n');
  };

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [listsRes, ordersRes, custRes, invRes, settingsRes] = await Promise.all([
        api.get('/sales/packing-lists'), api.get('/sales/orders'),
        api.get('/purchase/customers'), api.get('/sales/inventory'),
        api.get('/sales/company-settings')
      ]);
      setLists(listsRes.data); setOrders(ordersRes.data);
      setCustomers(custRes.data); setInventory(invRes.data);
      setCompanySettings(settingsRes.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const selectOrder = (orderId) => {
    const order = orders.find(o => o.id === orderId);
    if (order) {
      setSelectedOrder(order);
      const cust = customers.find(c => c.name === order.customer_name);
      // Address only (the print/header shows the customer name separately) — pull the
      // full billing address so the complete address is displayed, not just the name.
      const fullAddr = cust ? formatAddress(cust.billing_address) : '';
      setFormData(prev => ({
        ...prev,
        sales_order_id: orderId,
        customer_id: cust?.id || '',
        customer_name: order.customer_name,
        customer_address: fullAddr,
        items: order.items.map(item => ({ sku: item.sku, name: item.name, ordered_qty: item.quantity, pack_qty: item.quantity, box_number: '' }))
      }));
    }
  };

  const addBoxRow = () => {
    setFormData(prev => ({
      ...prev,
      box_rows: [...prev.box_rows, blankRow(prev.box_rows.length + 1)]
    }));
  };
  const num = (v) => { const n = parseFloat(v); return isNaN(n) ? null : n; };
  const updateBoxRow = (idx, field, val) => {
    setFormData(prev => {
      const rows = [...prev.box_rows];
      const row = { ...rows[idx], [field]: val };
      // Total Qty = Bundles (A) × Pieces/Bundle (B)
      if (field === 'bundles' || field === 'pieces_per_bundle') {
        const a = num(row.bundles), b = num(row.pieces_per_bundle);
        if (a !== null && b !== null) row.total_qty = String(a * b);
      }
      // Gross Weight auto-calc:
      //  • box-wise  = Net + Tare
      //  • roll-wise = Net + Shipper weight  (per client packing-list example)
      if (prev.pack_mode === 'roll') {
        if (field === 'net_weight' || field === 'shipper_weight') {
          const net = num(row.net_weight), ship = num(row.shipper_weight) || 0;
          if (net !== null) row.gross_weight = String(Number((net + ship).toFixed(3)));
        }
      } else if (field === 'net_weight' || field === 'tare_weight') {
        const net = num(row.net_weight), tare = num(row.tare_weight) || 0;
        if (net !== null) row.gross_weight = String(Number((net + tare).toFixed(3)));
      }
      rows[idx] = row;
      return { ...prev, box_rows: rows };
    });
  };
  const removeBoxRow = (idx) => {
    setFormData(prev => ({
      ...prev,
      box_rows: prev.box_rows.filter((_, i) => i !== idx).map((r, i) => ({ ...r, box_number: i + 1 }))
    }));
  };

  const openCreate = () => { setEditing(null); setFormData({ ...emptyForm }); setSelectedOrder(null); setShowModal(true); };

  const openEdit = (list) => {
    setEditing(list);
    // Old records saved the customer NAME (or a placeholder like "addr") into
    // customer_address. Prefer the real billing address from the customer master.
    const cust = customers.find(c => c.id === list.customer_id) || customers.find(c => c.name === list.customer_name);
    const stored = (list.customer_address || '').trim();
    const fullAddr = cust ? formatAddress(cust.billing_address) : '';
    const customerAddress = (stored && stored !== (list.customer_name || '').trim()) ? list.customer_address : (fullAddr || '');
    setFormData({
      sales_order_id: list.sales_order_id || '',
      customer_id: list.customer_id || '', customer_name: list.customer_name || '', customer_address: customerAddress,
      product_name: list.product_name || '', product_size: list.product_size || '',
      batch_number: list.batch_number || '', total_quantity: list.total_quantity || '', date: list.date || '',
      pack_mode: list.pack_mode || 'box',
      items: list.items || [],
      box_rows: (list.box_rows && list.box_rows.length > 0) ? list.box_rows.map(r => ({ ...blankRow(), ...r })) : [blankRow(1)],
      print_fields: list.print_fields || null
    });
    setShowModal(true);
  };

  // Which columns are selected for the printed packing list (default: all for the mode)
  const isFieldOn = (key) => {
    const pf = formData.print_fields;
    return !pf || pf.length === 0 ? true : pf.includes(key);
  };
  const toggleField = (key) => {
    setFormData(prev => {
      const all = colsForMode(prev.pack_mode).map(c => c.key);
      const current = (prev.print_fields && prev.print_fields.length) ? prev.print_fields : all;
      const next = current.includes(key) ? current.filter(k => k !== key) : [...current, key];
      return { ...prev, print_fields: next };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        const { customer_name, customer_address, product_name, product_size, batch_number, total_quantity, date, pack_mode, box_rows, print_fields } = formData;
        await api.put(`/sales/packing-lists/${editing.id}`, { customer_name, customer_address, product_name, product_size, batch_number, total_quantity, date, pack_mode, box_rows, print_fields });
        toast.success('Packing list updated');
      } else {
        await api.post('/sales/packing-lists', formData);
        toast.success('Packing list created');
      }
      setShowModal(false); setEditing(null); setSelectedOrder(null); setFormData({ ...emptyForm }); fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const approveList = async (id) => {
    try { await api.post(`/sales/packing-lists/${id}/approve`); fetchData(); toast.success('Packing list approved — labels auto-generated (see Labels)'); }
    catch (err) {
      const msg = err.response?.status === 403 ? 'You are not authorized to approve packing lists' : (err.response?.data?.detail || 'Failed');
      toast.error(msg);
    }
  };

  const printPacking = (list) => {
    const co = companySettings;
    const isRoll = (list.pack_mode || 'box') === 'roll';
    const td = (v) => `<td style="text-align:center;border:1px solid #000;padding:6px">${v ?? ''}</td>`;
    // Only the columns the user selected are printed (default = all for the mode).
    const allCols = isRoll ? ROLL_COLS : BOX_COLS;
    const pf = list.print_fields;
    const cols = (pf && pf.length) ? allCols.filter(c => pf.includes(c.key)) : allCols;
    // Lead column: Roll No. (roll mode) / Box # (box mode) as the row identifier.
    const leadHead = isRoll ? '<th>Roll No.</th>' : '<th>Box Number</th>';
    const leadVal = (r) => td(isRoll ? r.roll_number : r.box_number);
    const boxRows = (list.box_rows || []).map(r =>
      `<tr>${leadVal(r)}${cols.map(c => td(r[c.key])).join('')}</tr>`
    ).join('');
    const tableHead = leadHead + cols.map(c => `<th>${c.label}</th>`).join('');

    // Resolve the customer's full billing address from the customer master so the
    // print always shows the real address. Older records saved the customer NAME
    // (or a placeholder) into customer_address, which made the name appear twice.
    const cust = customers.find(c => c.id === list.customer_id) || customers.find(c => c.name === list.customer_name);
    const storedAddr = (list.customer_address || '').trim();
    const nameTrim = (list.customer_name || '').trim();
    const fullAddr = cust ? formatAddress(cust.billing_address) : '';
    const addrToShow = (storedAddr && storedAddr !== nameTrim) ? storedAddr : fullAddr;

    const w = window.open('', '_blank');
    w.document.write(`<html><head><title>Packing List - ${list.packing_number}</title>
    <style>
      @page{size:A4;margin:15mm}body{font-family:'Arial',sans-serif;font-size:12px;margin:0;padding:20px;color:#000}
      .header{display:flex;justify-content:space-between;align-items:flex-start;border-bottom:3px solid #000;padding-bottom:10px;margin-bottom:10px}
      .company-name{font-size:22px;font-weight:bold;color:#1a237e}
      .company-info{text-align:right;font-size:10px;line-height:1.5}
      .doc-title{text-align:center;font-size:18px;font-weight:bold;margin:12px 0;text-decoration:underline}
      .info-grid{display:flex;justify-content:space-between;margin-bottom:16px}
      .info-left{width:60%}.info-right{width:35%;text-align:right}
      .info-left p,.info-right p{margin:3px 0;font-size:11px}
      table{width:100%;border-collapse:collapse}
      th{background:#1a237e;color:#fff;padding:8px;border:1px solid #000;font-size:10px}
      @media print{button{display:none !important}}
    </style></head><body>
    <div class="header">
      <div><img src="https://hridya.dweb.co.in/hridayalogo.png" alt="logo" style="height:60px; width:auto;" /></div>
      <div class="company-info">${co.address || ''}<br/>${co.email || ''}<br/>${co.website || ''}</div>
    </div>
    <div class="doc-title">PACKING LIST</div>
    <div class="info-grid">
      <div class="info-left">
        <p><strong>TO,</strong></p>
        <p style="white-space:pre-line">${list.customer_name || ''}${addrToShow ? '\n' + addrToShow : ''}</p>
        <p style="margin-top:10px"><strong>Sub:</strong> Packing List - Reg.</p>
        <p style="margin-top:8px"><strong>Name of the Product:</strong> ${list.product_name || ''}</p>
        <p><strong>Size:</strong> ${list.product_size || ''}</p>
        <p><strong>Qty:</strong> ${list.total_quantity || ''}</p>
      </div>
      <div class="info-right">
        ${list.date ? `<p><strong>Date:</strong> ${list.date}</p>` : ''}
        <p><strong>Batch No:</strong> ${list.batch_number || ''}</p>
      </div>
    </div>
    <h3 style="text-align:center;font-size:13px;margin:16px 0 8px">${isRoll ? 'QUANTITY PER ROLL' : 'QUANTITY PER EACH BOX'}</h3>
    <table>
      <thead><tr>${tableHead}</tr></thead>
      <tbody>${boxRows}</tbody>
    </table>
    <button onclick="window.print()" style="margin-top:20px;padding:8px 24px;cursor:pointer">Print</button>
    </body></html>`);
    w.document.close();
  };

  const filtered = lists.filter(l =>
    (l.packing_number||'').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (l.customer_name||'').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (l.product_name||'').toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div data-testid="packing-page">
      <div className="flex items-center justify-between mb-4">
        <h1 style={{ fontSize: '24px', fontWeight: 700, margin: 0 }}>Packing Lists</h1>
        <button onClick={openCreate} className="btn btn-primary" data-testid="create-packing-btn"><Plus size={16} /> Create Packing List</button>
      </div>

      <div className="card mb-4">
        <div className="form-group" style={{ marginBottom: 0, position: 'relative' }}>
          <Search size={16} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-tertiary)' }} />
          <input type="text" className="form-input" placeholder="Search by packing #, customer, product..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} style={{ paddingLeft: '40px' }} data-testid="search-input" />
        </div>
      </div>

      <div className="card">
        {loading ? <div data-testid="loading-indicator">Loading...</div> : (
          <div className="table-container">
            <table>
              <thead><tr><th>Packing #</th><th>Customer</th><th>Product</th><th>Batch</th><th>Boxes</th><th>Status</th><th>Date</th><th>Actions</th></tr></thead>
              <tbody>
                {filtered.length === 0 ? <tr><td colSpan={8} style={{ textAlign: 'center', padding: '20px', color: 'var(--text-tertiary)' }}>No packing lists</td></tr> : filtered.map(list => (
                  <tr key={list.id} data-testid={`packing-row-${list.packing_number}`}>
                    <td className="font-mono">{list.packing_number}</td>
                    <td>{list.customer_name || '-'}</td>
                    <td>{list.product_name || '-'}</td>
                    <td>{list.batch_number || '-'}</td>
                    <td className="tabular-nums">{list.box_rows?.length || 0}</td>
                    <td><span className={`badge ${list.status === 'approved' ? 'badge-success' : 'badge-warning'}`}>{list.status === 'approved' ? 'Approved' : 'Draft'}</span></td>
                    <td className="text-sm">{list.date || new Date(list.packed_at).toLocaleDateString()}</td>
                    <td>
                      <div className="flex gap-2">
                        {list.status !== 'approved' && <button onClick={() => openEdit(list)} className="btn btn-ghost btn-sm" title="Edit" data-testid={`edit-btn-${list.packing_number}`}><Edit size={14} /></button>}
                        {list.status !== 'approved' && <button onClick={() => approveList(list.id)} className="btn btn-ghost btn-sm" title="Approve" data-testid={`approve-btn-${list.packing_number}`}><CheckCircle size={14} color="#059669" /></button>}
                        <button onClick={() => printPacking(list)} className="btn btn-ghost btn-sm" title="Print" data-testid={`print-btn-${list.packing_number}`}><Printer size={14} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showModal && (
        <div className="modal-overlay" data-testid="packing-modal">
          <div className="modal-content" style={{ maxWidth: '850px', maxHeight: '90vh', overflow: 'auto' }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header"><h2 style={{ margin: 0, fontSize: '18px', fontWeight: 700 }}>{editing ? `Edit Packing List ${editing.packing_number}` : 'Create Packing List'}</h2></div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="grid grid-cols-2" style={{ gap: '12px' }}>
                  <div className="form-group">
                    <label className="form-label">Sales Order *</label>
                    <select className="form-select" value={formData.sales_order_id} onChange={(e) => selectOrder(e.target.value)} required disabled={!!editing} data-testid="order-select">
                      <option value="">Select...</option>
                      {orders.filter(o => o.status !== 'cancelled').map(o => <option key={o.id} value={o.id}>{o.order_number} - {o.customer_name}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Date</label>
                    <input type="text" className="form-input" value={formData.date} onChange={(e) => setFormData({ ...formData, date: e.target.value })} placeholder="DD/MM/YYYY" data-testid="date-input" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Customer Name</label>
                    <input type="text" className="form-input" value={formData.customer_name} onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })} data-testid="customer-name" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Customer Address</label>
                    <textarea className="form-input" rows={2} value={formData.customer_address} onChange={(e) => setFormData({ ...formData, customer_address: e.target.value })} placeholder="Full billing address" data-testid="customer-address" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Product Name</label>
                    <input type="text" className="form-input" value={formData.product_name} onChange={(e) => setFormData({ ...formData, product_name: e.target.value })} data-testid="product-name" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Size</label>
                    <input type="text" className="form-input" value={formData.product_size} onChange={(e) => setFormData({ ...formData, product_size: e.target.value })} placeholder='e.g. 21" x 32"' data-testid="product-size" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Total Quantity</label>
                    <input type="text" className="form-input" value={formData.total_quantity} onChange={(e) => setFormData({ ...formData, total_quantity: e.target.value })} placeholder="e.g. 2026.52 kgs / 14,700 nos" data-testid="total-qty" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Batch No</label>
                    <input type="text" className="form-input" value={formData.batch_number} onChange={(e) => setFormData({ ...formData, batch_number: e.target.value })} data-testid="batch-input" />
                  </div>
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '20px 0 10px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <h3 style={{ fontSize: '14px', fontWeight: 700, margin: 0 }}>{formData.pack_mode === 'roll' ? 'Quantity Per Roll' : 'Quantity Per Each Box'}</h3>
                    <select className="form-select" value={formData.pack_mode} onChange={(e) => setFormData({ ...formData, pack_mode: e.target.value })} style={{ fontSize: 12, padding: '4px 8px', width: 'auto' }} data-testid="pack-mode">
                      <option value="box">Box-wise</option>
                      <option value="roll">Roll-wise</option>
                    </select>
                  </div>
                  <button type="button" onClick={addBoxRow} className="btn btn-secondary btn-sm" data-testid="add-box-row"><Plus size={12} /> Add {formData.pack_mode === 'roll' ? 'Roll' : 'Box'}</button>
                </div>
                <table style={{ width: '100%', fontSize: '12px', borderCollapse: 'collapse' }}>
                  {formData.pack_mode === 'roll' ? (
                    <>
                      <thead><tr>
                        <th style={{ padding: '6px' }}>Roll No.</th>
                        <th style={{ padding: '6px' }}>Number of Rolls</th>
                        <th style={{ padding: '6px' }}>Net Wt (Kg)</th>
                        <th style={{ padding: '6px' }}>Shipper Wt (Kg)</th>
                        <th style={{ padding: '6px' }}>Gross Wt (Kg)</th>
                        <th style={{ padding: '6px', width: '40px' }}></th>
                      </tr></thead>
                      <tbody>
                        {formData.box_rows.map((row, idx) => (
                          <tr key={idx}>
                            <td style={{ padding: '4px' }}><input type="text" className="form-input" value={row.roll_number || ''} onChange={(e) => updateBoxRow(idx, 'roll_number', e.target.value)} style={{ fontSize: '12px', padding: '4px' }} /></td>
                            <td style={{ padding: '4px' }}><input type="text" className="form-input" value={row.number_of_rolls || ''} onChange={(e) => updateBoxRow(idx, 'number_of_rolls', e.target.value)} style={{ fontSize: '12px', padding: '4px' }} /></td>
                            <td style={{ padding: '4px' }}><input type="text" className="form-input" value={row.net_weight} onChange={(e) => updateBoxRow(idx, 'net_weight', e.target.value)} style={{ fontSize: '12px', padding: '4px' }} /></td>
                            <td style={{ padding: '4px' }}><input type="text" className="form-input" value={row.shipper_weight || ''} onChange={(e) => updateBoxRow(idx, 'shipper_weight', e.target.value)} placeholder="0" style={{ fontSize: '12px', padding: '4px' }} /></td>
                            <td style={{ padding: '4px' }}><input type="text" className="form-input" value={row.gross_weight} readOnly title="Auto = Net + Shipper" style={{ fontSize: '12px', padding: '4px', background: '#f3f4f6' }} /></td>
                            <td style={{ padding: '4px' }}>{formData.box_rows.length > 1 && <button type="button" onClick={() => removeBoxRow(idx)} className="btn btn-ghost btn-sm" style={{ color: 'red' }}>x</button>}</td>
                          </tr>
                        ))}
                      </tbody>
                    </>
                  ) : (
                    <>
                      <thead><tr>
                        <th style={{ padding: '6px', width: '60px' }}>Box #</th>
                        <th style={{ padding: '6px' }}>Number of Boxes</th>
                        <th style={{ padding: '6px' }}>Bundles (A)</th>
                        <th style={{ padding: '6px' }}>Pieces/Bundle (B)</th>
                        <th style={{ padding: '6px' }}>Total Qty (AxB)</th>
                        <th style={{ padding: '6px' }}>Net Wt (Kg)</th>
                        <th style={{ padding: '6px' }}>Tare (Kg)</th>
                        <th style={{ padding: '6px' }}>Total Wt of Bundle (Kg)</th>
                        <th style={{ padding: '6px', width: '40px' }}></th>
                      </tr></thead>
                      <tbody>
                        {formData.box_rows.map((row, idx) => (
                          <tr key={idx}>
                            <td style={{ padding: '4px', textAlign: 'center' }}>{row.box_number}</td>
                            <td style={{ padding: '4px' }}><input type="text" className="form-input" value={row.num_boxes || ''} onChange={(e) => updateBoxRow(idx, 'num_boxes', e.target.value)} placeholder="1" style={{ fontSize: '12px', padding: '4px' }} /></td>
                            <td style={{ padding: '4px' }}><input type="text" className="form-input" value={row.bundles} onChange={(e) => updateBoxRow(idx, 'bundles', e.target.value)} style={{ fontSize: '12px', padding: '4px' }} /></td>
                            <td style={{ padding: '4px' }}><input type="text" className="form-input" value={row.pieces_per_bundle} onChange={(e) => updateBoxRow(idx, 'pieces_per_bundle', e.target.value)} style={{ fontSize: '12px', padding: '4px' }} /></td>
                            <td style={{ padding: '4px' }}><input type="text" className="form-input" value={row.total_qty} onChange={(e) => updateBoxRow(idx, 'total_qty', e.target.value)} style={{ fontSize: '12px', padding: '4px' }} /></td>
                            <td style={{ padding: '4px' }}><input type="text" className="form-input" value={row.net_weight} onChange={(e) => updateBoxRow(idx, 'net_weight', e.target.value)} style={{ fontSize: '12px', padding: '4px' }} /></td>
                            <td style={{ padding: '4px' }}><input type="text" className="form-input" value={row.tare_weight || ''} onChange={(e) => updateBoxRow(idx, 'tare_weight', e.target.value)} placeholder="0" style={{ fontSize: '12px', padding: '4px' }} /></td>
                            <td style={{ padding: '4px' }}><input type="text" className="form-input" value={row.gross_weight} readOnly title="Auto = Net + Tare" style={{ fontSize: '12px', padding: '4px', background: '#f3f4f6' }} /></td>
                            <td style={{ padding: '4px' }}>{formData.box_rows.length > 1 && <button type="button" onClick={() => removeBoxRow(idx)} className="btn btn-ghost btn-sm" style={{ color: 'red' }}>x</button>}</td>
                          </tr>
                        ))}
                      </tbody>
                    </>
                  )}
                </table>
                <p style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 6 }}>Gross Weight is auto-calculated ({formData.pack_mode === 'roll' ? 'Net + Shipper' : 'Net + Tare'}).</p>

                <div style={{ marginTop: 14, padding: '10px 12px', background: 'var(--bg-surface-alt, #f8fafc)', borderRadius: 6 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 6 }}>Fields to show on the printed packing list</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                    {colsForMode(formData.pack_mode).map(c => (
                      <label key={c.key} style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 4 }}>
                        <input type="checkbox" checked={isFieldOn(c.key)} onChange={() => toggleField(c.key)} data-testid={`pf-${c.key}`} /> {c.label}
                      </label>
                    ))}
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" onClick={() => setShowModal(false)} className="btn btn-secondary">Cancel</button>
                <button type="submit" className="btn btn-primary" data-testid="submit-packing-btn">{editing ? 'Update Packing List' : 'Create Packing List'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default PackingList;
